import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { useEffect, useRef } from 'react';

/* ─── MOUSE PARALLAX HOOK ─── */
function useMouseParallax(strength = 20) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 40, stiffness: 90, mass: 1.5 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMove = (e) => {
      const cx = (e.clientX / window.innerWidth - 0.5) * 2;
      const cy = (e.clientY / window.innerHeight - 0.5) * 2;
      mouseX.set(cx * strength);
      mouseY.set(cy * strength);
    };
    window.addEventListener('mousemove', handleMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMove);
  }, [mouseX, mouseY, strength]);

  return { x, y };
}

/* ─── STAR FIELD — Derinlik katmanları ─── */
const StarField = () => {
  const layers = [
    { count: 60, sizeRange: [1, 2], speed: 0.3, opacity: 0.3 },
    { count: 30, sizeRange: [2, 4], speed: 0.6, opacity: 0.5 },
    { count: 12, sizeRange: [3, 5], speed: 1, opacity: 0.7 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 15 }}>
      {layers.map((layer, li) =>
        Array.from({ length: layer.count }, (_, i) => {
          const x = Math.random() * 100;
          const y = Math.random() * 100;
          const size = layer.sizeRange[0] + Math.random() * (layer.sizeRange[1] - layer.sizeRange[0]);
          const dur = 15 + Math.random() * 25;
          const delay = Math.random() * dur;
          const drift = layer.speed * (30 + Math.random() * 40);

          return (
            <motion.div
              key={`star-${li}-${i}`}
              className="absolute rounded-full"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: size,
                height: size,
                backgroundColor: 'white',
                boxShadow: size > 3 ? `0 0 ${size * 2}px rgba(255,255,255,0.4)` : 'none',
              }}
              animate={{
                x: [0, drift * 0.4, -drift * 0.3, drift * 0.2, 0],
                y: [0, -drift * 0.3, drift * 0.5, -drift * 0.2, 0],
                opacity: [0, layer.opacity, layer.opacity * 0.5, layer.opacity * 0.8, 0],
              }}
              transition={{
                duration: dur,
                delay,
                repeat: Infinity,
                ease: 'linear',
              }}
            />
          );
        })
      )}
    </div>
  );
};

/* ─── SPACE DEBRIS — Yavaşça dönen parçacıklar ─── */
const SpaceDebris = () => {
  const pieces = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    x: 25 + Math.random() * 70,
    y: Math.random() * 100,
    w: 2 + Math.random() * 6,
    h: 1 + Math.random() * 2,
    dur: 20 + Math.random() * 35,
    delay: Math.random() * 20,
    drift: 40 + Math.random() * 60,
    rot: Math.random() * 360,
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 18 }}>
      {pieces.map((p) => (
        <motion.div
          key={`debris-${p.id}`}
          className="absolute"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.w,
            height: p.h,
            backgroundColor: 'rgba(200, 210, 220, 0.4)',
            borderRadius: 1,
          }}
          animate={{
            x: [0, p.drift * 0.6, -p.drift * 0.4, p.drift * 0.3, 0],
            y: [0, -p.drift * 0.5, p.drift * 0.7, -p.drift * 0.2, 0],
            rotate: [0, p.rot, p.rot * 2, p.rot * 3],
            opacity: [0, 0.5, 0.3, 0.6, 0],
          }}
          transition={{
            duration: p.dur,
            delay: p.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </div>
  );
};

/* ─── COSMIC LIGHT RAYS ─── */
const CosmicRays = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 4 }}>
    <motion.div
      className="absolute top-0 right-1/4 w-[600px] h-[800px]"
      style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(6,182,212,0.25) 30%, rgba(139,92,246,0.15) 60%, transparent 100%)',
        filter: 'blur(60px)',
        transformOrigin: 'top center',
      }}
      animate={{ rotate: [-5, 5, -5], opacity: [0.12, 0.22, 0.12] }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div
      className="absolute top-0 right-1/3 w-[400px] h-[600px]"
      style={{
        background: 'linear-gradient(180deg, transparent 0%, rgba(236,72,153,0.15) 40%, rgba(139,92,246,0.1) 70%, transparent 100%)',
        filter: 'blur(80px)',
        transformOrigin: 'top center',
      }}
      animate={{ rotate: [3, -3, 3], opacity: [0.08, 0.18, 0.08] }}
      transition={{ duration: 6, delay: 1, repeat: Infinity, ease: 'easeInOut' }}
    />
  </div>
);

/* ─── ANA HERO BACKGROUND ─── */
export const DynamicHeroBackground = ({ imageUrl }) => {
  const parallax = useMouseParallax(18);
  const deepParallax = useMouseParallax(8);

  return (
    <div className="absolute inset-0 overflow-hidden">

      {/* ── Astronot — Mouse parallax + organik yüzme + nefes efekti ── */}
      <motion.div
        className="absolute z-[1]"
        style={{
          top: '-4%',
          left: '-4%',
          right: '-4%',
          bottom: '-4%',
          x: parallax.x,
          y: parallax.y,
        }}
      >
        {/* Organik yüzme + nefes (scale) */}
        <motion.div
          className="absolute inset-0"
          animate={{
            x: [0, 12, -8, 15, -6, 10, -4, 0],
            y: [0, -10, 6, -14, 8, -5, 12, 0],
            rotate: [0, 0.4, -0.3, 0.6, -0.5, 0.2, -0.4, 0],
            scale: [1, 1.015, 1.005, 1.02, 1.008, 1.018, 1.003, 1],
          }}
          transition={{
            duration: 40,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(${imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center right',
              backgroundRepeat: 'no-repeat',
            }}
          />
        </motion.div>
      </motion.div>

      {/* ── Karartma Overlay ── */}
      <div className="absolute inset-0 z-[2]">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/95 via-[#0A0A0A]/70 to-[#0A0A0A]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/30" />
      </div>

      {/* ── Cosmic Rays ── */}
      <CosmicRays />

      {/* ── Star Field (3 derinlik katmanı) ── */}
      <StarField />

      {/* ── Space Debris ── */}
      <SpaceDebris />

      {/* ── Astronot etrafı parlama ── */}
      <motion.div
        className="absolute z-[5] pointer-events-none"
        style={{
          top: '15%',
          right: '15%',
          width: 400,
          height: 400,
          background: 'radial-gradient(circle, rgba(6,182,212,0.12) 0%, transparent 70%)',
          filter: 'blur(50px)',
          x: deepParallax.x,
          y: deepParallax.y,
        }}
        animate={{ scale: [1, 1.3, 1], opacity: [0.25, 0.45, 0.25] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* ── Nebula ── */}
      <motion.div
        className="absolute bottom-0 left-0 z-[5] pointer-events-none"
        style={{
          width: 600,
          height: 400,
          background: 'radial-gradient(ellipse at bottom left, rgba(139,92,246,0.25) 0%, transparent 60%)',
          filter: 'blur(60px)',
        }}
        animate={{ x: [-20, 20, -20], opacity: [0.15, 0.3, 0.15] }}
        transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
};

export default DynamicHeroBackground;
